import { useState, Component } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Box, AppBar, Toolbar, Typography, Button, Dialog, DialogTitle, DialogContent,
  Drawer, List, ListItem, ListItemIcon, ListItemText
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import LogoutIcon from "@mui/icons-material/Logout";
import HistoryIcon from "@mui/icons-material/History";
import LockIcon from "@mui/icons-material/Lock";
import PeopleIcon from "@mui/icons-material/People";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import QuizIcon from "@mui/icons-material/Quiz";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BarChartIcon from "@mui/icons-material/BarChart";
import UserManagement from "../components/trainer/UserManagement";
import LearningPathManager from "../components/trainer/LearningPathManager";
import ModuleBuilder from "../components/trainer/ModuleBuilder";
import QuestionBank from "../components/trainer/QuestionBank";
import EnrolmentManager from "../components/trainer/EnrolmentManager";
import AgentProgress from "../components/trainer/AgentProgress";
import QuestionAnalysis from "../components/trainer/QuestionAnalysis";
import AnalyticsDashboard from "../components/trainer/AnalyticsDashboard";
import TeamManagement from "../components/trainer/TeamManagement";
import ActivityLog from "../components/trainer/ActivityLog";

const DRAWER_WIDTH = 240;

class ErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6">Something went wrong in this section.</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>{this.state.error?.message}</Typography>
          <Button onClick={() => this.setState({ hasError: false })} sx={{ mt: 2 }} variant="outlined">Try Again</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function TrainerDashboard() {
  const { user, logout } = useAuth();
  const [activityOpen, setActivityOpen] = useState(false);
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isTrainer = user?.role === "trainer";
  const [activeSection, setActiveSection] = useState(1);

  const sections = [
    ...((isAdmin || isTrainer) ? [{ label: "Users", icon: <PeopleIcon />, index: 0 }] : []),
    ...(isAdmin ? [{ label: "Teams", icon: <PeopleIcon />, index: 8 }] : []),
    { label: "Learning Paths", icon: <LibraryBooksIcon />, index: 1 },
    { label: "Module Builder", icon: <VideoLibraryIcon />, index: 2 },
    { label: "Question Bank", icon: <QuizIcon />, index: 3 },
    { label: "Enrolments", icon: <AssignmentIcon />, index: 4 },
    { label: "Agent Progress", icon: <BarChartIcon />, index: 5 },
    { label: "Analytics", icon: <BarChartIcon />, index: 6 },
  ];

  const renderSection = () => {
    switch(activeSection) {
      case 0: return <ErrorBoundary><UserManagement /></ErrorBoundary>;
      case 1: return <ErrorBoundary><LearningPathManager /></ErrorBoundary>;
      case 2: return <ErrorBoundary><ModuleBuilder /></ErrorBoundary>;
      case 3: return <ErrorBoundary><QuestionBank /></ErrorBoundary>;
      case 4: return <ErrorBoundary><EnrolmentManager /></ErrorBoundary>;
      case 5: return <ErrorBoundary><AgentProgress /></ErrorBoundary>;
      case 6: return <ErrorBoundary><AnalyticsDashboard /></ErrorBoundary>;
      case 8: return <ErrorBoundary><TeamManagement /></ErrorBoundary>;

      default: return <LearningPathManager />;
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <AppBar position="fixed" sx={{
        zIndex: 1201,
        background: "linear-gradient(135deg, #1a237e, #0d47a1)"
      }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Alteram — Trainer Admin Panel
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>
            {user?.full_name}
          </Typography>
          <Button color="inherit" onClick={() => navigate('/change-password')} startIcon={<LockIcon />} sx={{ mr: 1 }}>
  Change Password
</Button>
{user?.role === "admin" && (
            <Button color="inherit" onClick={() => setActivityOpen(true)} startIcon={<HistoryIcon />} sx={{ mr: 1 }}>
              Activity Log
            </Button>
          )}
<Button color="inherit" onClick={logout} startIcon={<LogoutIcon />}>
  Sign Out
          </Button>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: DRAWER_WIDTH,
          boxSizing: "border-box",
          mt: 8,
          bgcolor: "#f8f9fa",
          borderRight: "1px solid #e0e0e0"
        },
      }}>
        <List sx={{ pt: 2 }}>
          {sections.map((section) => (
            <ListItem
              button
              key={section.index}
              onClick={() => setActiveSection(section.index)}
              sx={{
                mb: 0.5, mx: 1, borderRadius: 2,
                bgcolor: activeSection === section.index ? "#e8eaf6" : "transparent",
                color: activeSection === section.index ? "#1a237e" : "inherit",
                "&:hover": { bgcolor: "#e8eaf6" }
              }}
            >
              <ListItemIcon sx={{
                color: activeSection === section.index ? "#1a237e" : "inherit",
                minWidth: 40
              }}>
                {section.icon}
              </ListItemIcon>
              <ListItemText primary={section.label} primaryTypographyProps={{
                fontWeight: activeSection === section.index ? 700 : 400
              }} />
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{
        flexGrow: 1, p: 3, mt: 8,
        bgcolor: "#f5f7fa", minHeight: "100vh"
      }}>
        {renderSection()}
      </Box>
    {/* Activity Log Dialog */}
    <Dialog open={activityOpen} onClose={() => setActivityOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: "#1a237e", display: "flex", justifyContent: "space-between", alignItems: "center" }}>Activity Log<Button onClick={() => setActivityOpen(false)} size="small" variant="outlined">Close</Button></DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <ActivityLog />
      </DialogContent>
    </Dialog>
    </Box>
  );
}
